import React, { useState, useEffect } from 'react';
import { ModalWrapper, ButtonGroup } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, Button, Input, Space, Row, Col, Select } from 'antd';
import { CheckOutlined } from '@ant-design/icons'
import { useB2cNextCode, useB2cStudentDetail } from 'hooks';

export interface StudentReinforceModalProps extends PopupProps {
    lectureInfo: LectureInfoDto;
    id: number;
    group: string;
}

const StudentReinforceModal = ({ 
    isOpen, 
    onClose, 
    lectureInfo, id, group}: StudentReinforceModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
    const [supplyPeriod, setSupplyPeriod] = useState<string>('');
    const [nonAttendList, setNonAttendList] = useState<any[]>([]);
    const [nextAttendList, setNextAttendList] = useState<StudentAttendInfoDto[]>([]);
    const [nextNonAttendSchedule, setNextNonAttendSchedule] = useState<number[]>([]);
  /* ======================================================
  * hooks
  * ====================================================== */

    const { codeList } = useB2cNextCode({lecture_gid: lectureInfo.lecture_gid, lecture_cha: lectureInfo.lecture_cha })
    const { studentInfo, studentReinforce, reinforceSuccess, studentAttendList  } = useB2cStudentDetail(id, group);

   /* ======================================================
   * variables
   * ====================================================== */
  const [form] = Form.useForm();

   /* ======================================================
   * events
   * ====================================================== */

    const onSubmit = (e) => {
        if (!!studentInfo) {
            studentReinforce({
                id: studentInfo.id,
                history_memo: e.history_memo,
                next_group_srl: e.next_group_srl,
                next_non_attend: nextNonAttendSchedule,
            })                        
        }
    };

    const onCloseClick = () => {
        onClose?.(reinforceSuccess);
    }

    const toggleAttend = (value: number) => {
        setNextNonAttendSchedule((prev) => {
            // 1. 이미 값이 포함되어 있는지 확인
            if (prev.includes(value)) {
            // 값이 있으면 제외 (filter 사용)
            return prev.filter((item) => item !== value);
            } else {
            // 값이 없으면 추가 (새 배열 반환)
            return [...prev, value];
            }
    });
    };    
  /* ======================================================
   * effects
   * ====================================================== */
  useEffect(() => {
    if (reinforceSuccess) {   
        onClose?.(true);
    }
    }, [reinforceSuccess])

  useEffect(() => {
    const newList = studentAttendList.filter(item => item.history_id > 0);
    setNonAttendList(newList);
    }, [studentAttendList])    
    
    return (
    <ModalWrapper open={isOpen} onCancel={onCloseClick} width={1000} title={'공개과정 교육생 보강'} closable>
        <Space direction='vertical' style={{width:'100%'}}>
            <h4 style={{marginBottom:'0px'}}>{lectureInfo.lecture_title}</h4>
            <label ><CheckOutlined /> 신청자정보</label>
            <Form
                form={form}
                name="StudentInfo"
                layout="horizontal"  
                labelCol={{ flex: '120px' }}
                onFinish={onSubmit}
                style={{width:'100%'}}
                autoComplete="off"
            >
                <Row>
                    <Col span={12}>
                    <Form.Item label={'고객사명'}>
                        <label>{studentInfo?.company}</label>                  
                    </Form.Item> 
                    </Col> 
                    <Col span={12}> 
                    <Form.Item label={'신청자'}>
                        <label>{studentInfo?.name_kor}</label>                  
                    </Form.Item>   
                    </Col>            
                </Row>
                {nonAttendList.length > 0 && (
                <>
                    <Form.Item label={'기존불참이력'}>
                    {nonAttendList.map((item)=> 
                        <Row key={item.schedule_srl} style={{marginTop:3}}>
                            <Col span={7}>
                                {item.schedule_time}
                            </Col>
                            <Col span={3} style={{alignItems:'center'}}>
                                {item.lecturer}
                            </Col>                            
                            <Col span={13}>
                                <label>{item.lecture_subject}</label>
                            </Col>                            
                        </Row>
                    )}
                    </Form.Item>
                    <Form.Item label={'보강불참이력'}>
                        {nextAttendList.map((item)=> 
                            <Row key={item.schedule_srl} style={{marginTop:3}}>
                                <Col span={7}>
                                    {item.schedule_time}
                                </Col>
                                <Col span={3} style={{alignItems:'center'}}>
                                    {item.lecturer}
                                </Col>                            
                                <Col span={10}>
                                    <label>{item.lecture_subject}</label>
                                </Col>                            
                                <Col span={3} style={{width:'fit-content', marginLeft:'auto'}}>
                                    <Button 
                                        size='small' 
                                        type="default" 
                                        danger={nextNonAttendSchedule.includes(item.schedule_srl)} 
                                        onClick={() => 
                                            toggleAttend(item.schedule_srl)
                                        }
                                    >{nextNonAttendSchedule.includes(item.schedule_srl) ? '불참':'참석'}</Button>
                                </Col>                            
                            </Row>
                        )}
                    </Form.Item>                     
                </>
                )}
                <Form.Item name={'history_memo'} label={'보강사유'} rules={[{ required: true, message: '' }]}>
                    <Input type="text" placeholder='' />                 
                </Form.Item>  
                <Row>
                    <Col span={12}>
                    <Form.Item label={'교육담당자'}>
                        <label>{studentInfo?.edu_name}</label>                  
                    </Form.Item> 
                    </Col> 
                    <Col span={12}> 
                    <Form.Item label={'담당자연락처'}>
                        <label>{studentInfo?.edu_phone})</label>                  
                    </Form.Item>   
                    </Col>            
                </Row>
                <Form.Item label={'기존일정'}>
                    <label>{lectureInfo.lecture_title} - 기간 : {lectureInfo.start_date} ~ {lectureInfo.end_date}</label>                    
                </Form.Item>   
                <Form.Item name="next_group_srl" label={'변경일정'} rules={[{ required: true, message: '' }]}>
                    <Select 
                        placeholder="변경일정 선택" 
                        options={codeList} 
                        onSelect={(value, option) => {
                            const scheduleList = option.schedules.split('||');
                            const newAttendList = scheduleList.map((item) => {
                                const infoList = item.split('//');

                                return {
                                    history_id: 0,
                                    student_id: studentInfo?.id,
                                    b2c_group_srl: value,
                                    schedule_srl: Number(infoList[0]),
                                    schedule_time: infoList[1],
                                    lecturer: infoList[2],
                                    lecture_subject: infoList[3]
                                };
                            });
                            setNextNonAttendSchedule([]);
                            setNextAttendList(newAttendList as StudentAttendInfoDto[]);
                            setSupplyPeriod(option.lecture_period);
                        }} />                       
                </Form.Item>                   

                <ButtonGroup className={'buttonsWrapper'}>
                    <Button onClick={onCloseClick} style={{marginRight: "5px"}}>취소</Button>                    
                    <Button htmlType="submit"  type="primary">{'확인'}</Button>                    
                </ButtonGroup>                 
            </Form>
        </Space>        
    </ModalWrapper>  
    )    
};

export default React.memo(StudentReinforceModal);    